import {
  AfterViewInit,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ApisService } from "@core/services/apis.service";
import { LoadingService } from "@core/services/loading.service";
import { ToastrService } from "ngx-toastr";

@Component({
  selector: "app-chat-view",
  templateUrl: "./chat-view.component.html",
  styleUrls: ["./chat-view.component.scss"],
})
export class ChatViewComponent implements OnInit, AfterViewInit {
  // ** Variables **
  public chats: any[] = [];

  public contacts: any = [];

  public messages: any[] = [];
  public isView: boolean = false;

  public activeContact: any = null;
  public newMessage: string = "";
  public searchText: string = "";
  public selectedFile: File | null = null;
  public filePreview: string | null = null;
  public previewImageUrl: string | null = null;
  public fakeUsersOptions: any[] = [];
  public usersOptions: any[] = [];
  public fakeChatForm = {
    fake_user_id: null as number | null,
    user_id: null as number | null,
  };
  public sendAsUserId: number | null = null;

  // Pagination (WhatsApp-like): load latest 20, then load older on scroll up
  private currentPage: number = 1;
  private perPage: number = 20;
  public isLoadingMore: boolean = false;
  public hasMore: boolean = true;
  private readonly adminUserId = 1;
  @ViewChild("userChats")
  set userChatsSetter(content: ElementRef) {
    if (content) {
      this.userChats = content;
      this.scrollToBottom();
    }
  }
  userChats!: ElementRef;
  constructor(
    private apiService: ApisService,
    private loading: LoadingService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    const userId = Number(this.route.snapshot.queryParamMap.get("user_id"));
    const fakeUserId = Number(this.route.snapshot.queryParamMap.get("fake_user_id"));

    this.loadChatStartOptions();

    if (userId && fakeUserId) {
      this.fakeChatForm = {
        fake_user_id: fakeUserId,
        user_id: userId,
      };
      this.openFakeChat();
    } else if (userId) {
      this.openChatWithUser(userId);
    } else {
      this.viewChats();
    }
  }

  ngAfterViewInit(): void {
    this.scrollToBottom();
  }

  viewChats() {
    this.apiService.viewChats().subscribe({
      next: (res) => {
        this.chats = this.extractCollection(res);
        console.log(this.filteredContacts);
      },
      error: (err) => {
        console.log(err);
        this.toastr.error("فشل تحميل المحادثات");
      },
    });
  }

  openChatWithUser(userId: number) {
    this.loading.show();
    this.apiService.openAdminChatWithUser(userId).subscribe({
      next: (res: any) => {
        const room = res?.data || res;
        this.activeContact = room;
        this.chats = [room];
        this.setDefaultSendAsUser(room);
        this.getMessages(room.id);
      },
      error: (err) => {
        console.log(err);
        this.toastr.error("فشل فتح الشات مع المستخدم");
        this.loading.hide();
        this.viewChats();
      },
    });
  }

  openFakeChat() {
    const fakeUserId = Number(this.fakeChatForm.fake_user_id);
    const userId = Number(this.fakeChatForm.user_id);

    if (!fakeUserId || !userId) {
      this.toastr.warning("اختار المستخدم الوهمي والمستخدم الحقيقي");
      return;
    }

    if (fakeUserId === userId) {
      this.toastr.warning("لا يمكن فتح شات بين نفس المستخدم");
      return;
    }

    this.loading.show();
    this.apiService.openFakeChatWithUser(fakeUserId, userId).subscribe({
      next: (res: any) => {
        const room = res?.data || res;
        this.activeContact = room;
        this.chats = [room, ...this.toArray(this.chats).filter((item) => Number(item?.id) !== Number(room?.id))];
        this.setDefaultSendAsUser(room, fakeUserId);
        this.getMessages(room.id);
      },
      error: (err) => {
        console.log(err);
        this.toastr.error("فشل فتح الشات بين المستخدمين");
        this.loading.hide();
      },
    });
  }

  getMessages(id: number) {
    // Initial load: latest 20
    this.loading.show();
    this.currentPage = 1;
    this.hasMore = true;

    this.apiService.roomMessages(id, this.currentPage, this.perPage, true).subscribe({
      next: (res) => {
        // Laravel paginator returns {data: [...], meta: {...}}
        this.messages = this.extractCollection(res);
        this.updatePaginationState(res);
        this.scrollToBottom();
      },
      error: (err) => {
        console.log(err);
      },
      complete: () => {
        this.loading.hide();
      },
    });
  }

  loadOlderMessages() {
    if (!this.activeContact || this.isLoadingMore || !this.hasMore) return;

    const roomId = this.activeContact.id;
    const container = this.userChats?.nativeElement;
    const prevScrollHeight = container ? container.scrollHeight : 0;

    this.isLoadingMore = true;
    const nextPage = this.currentPage + 1;

    this.apiService.roomMessages(roomId, nextPage, this.perPage, true).subscribe({
      next: (res) => {
        const older = this.extractCollection(res);
        // Prepend older messages
        this.messages = [...older, ...this.messages];

        this.updatePaginationState(res);
        this.currentPage = nextPage;

        // keep scroll position stable after prepend
        setTimeout(() => {
          if (container) {
            const newScrollHeight = container.scrollHeight;
            container.scrollTop = newScrollHeight - prevScrollHeight;
          }
        }, 0);
      },
      error: (err) => console.log(err),
      complete: () => (this.isLoadingMore = false),
    });
  }

  selectChat(contact: any) {
    this.activeContact = contact;
    this.setDefaultSendAsUser(contact);
    this.getMessages(contact.id);
  }

  sendMessage() {
    if (!this.activeContact || this.newMessage.trim() === "") return;

    const formData = new FormData();
    formData.append("message", this.newMessage.trim());
    formData.append("type", "text");

    const senderId = Number(this.sendAsUserId);
    const shouldSendAsRoomUser = senderId && senderId !== this.adminUserId;

    if (shouldSendAsRoomUser) {
      formData.append("from_user_id", String(senderId));
    }

    this.loading.show();
    const request$ = shouldSendAsRoomUser
      ? this.apiService.sendChatMessageAsUser(this.activeContact.id, formData)
      : this.apiService.sendAdminChatMessage(this.activeContact.id, formData);

    request$.subscribe({
      next: (res: any) => {
        const message = res?.data;
        if (message) {
          this.messages = [...this.messages, message];
        }
        this.newMessage = "";
        this.scrollToBottom();
      },
      error: (err) => {
        console.log(err);
        this.toastr.error("فشل إرسال الرسالة");
      },
      complete: () => this.loading.hide(),
    });
  }

  get activeRoomUsers(): any[] {
    return this.toArray(this.activeContact?.users);
  }

  get filteredContacts() {
    const q = (this.searchText || "").toLowerCase();

    return this.toArray(this.chats).filter((c) =>
      this.getRoomDisplayName(c)?.toLowerCase().includes(q),
    );
  }

  getTargetUser(room: any): any {
    const users = this.toArray(room?.users);
    return users.find((user: any) => Number(user?.id) !== this.adminUserId) || users[0] || null;
  }

  getRoomDisplayName(room: any): string {
    const users = this.toArray(room?.users);
    const hasAdminUser = users.some((user: any) => Number(user?.id) === this.adminUserId);
    const targetUserName = this.getTargetUser(room)?.name;

    if (hasAdminUser && targetUserName) {
      return targetUserName;
    }

    return room?.display_name || targetUserName || "-";
  }

  getRoomImage(room: any): string {
    return this.getTargetUser(room)?.image || "assets/images/icons/user.svg";
  }

  isUserMessage(msg: any): boolean {
    const senderId = this.getMessageSenderId(msg);
    return !!senderId && senderId === this.getLeftSideUserId(this.activeContact);
  }

  getMessageAvatar(msg: any): string {
    const sender = this.getMessageSender(msg);

    if (Number(sender?.id) === this.adminUserId) {
      return "assets/images/icons/logo.jpg";
    }

    return sender?.image || "assets/images/icons/user.svg";
  }

  getMessageSenderName(msg: any): string {
    return this.getMessageSender(msg)?.name || "مستخدم";
  }

  isImageMessage(msg: any): boolean {
    const filePath = (msg?.file_path || "").split("?")[0].toLowerCase();
    const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp", ".bmp"];

    return msg?.type === "image" || imageExtensions.some((ext) => filePath.endsWith(ext));
  }

  openImagePreview(imageUrl: string | null | undefined): void {
    if (!imageUrl) {
      return;
    }

    this.previewImageUrl = imageUrl;
  }

  closeImagePreview(): void {
    this.previewImageUrl = null;
  }

  private extractCollection(res: any): any[] {
    return this.toArray(res?.data?.data || res?.data || []);
  }

  private updatePaginationState(res: any): void {
    const meta = res?.meta || res?.data?.meta || {};
    const links = res?.links || res?.data?.links || {};
    const currentPage = Number(meta.current_page || this.currentPage);
    const lastPage = Number(meta.last_page || currentPage);

    this.hasMore = !!(
      links.next ||
      meta.next_page_url ||
      (Number.isFinite(currentPage) && Number.isFinite(lastPage) && currentPage < lastPage)
    );
  }

  private getMessageSender(msg: any): any {
    const senderId = this.getMessageSenderId(msg);
    const users = this.toArray(this.activeContact?.users);

    return msg?.from_user || users.find((user: any) => Number(user?.id) === senderId) || null;
  }

  private getMessageSenderId(msg: any): number | null {
    const senderId = Number(msg?.from_user?.id || msg?.from_user_id);
    return Number.isFinite(senderId) ? senderId : null;
  }

  private getLeftSideUserId(room: any): number | null {
    const users = this.toArray(room?.users);

    if (!users.length) {
      return null;
    }

    const targetUser = users.find((user: any) => Number(user?.id) !== this.adminUserId);
    const leftSideUser = targetUser || users[0];
    const leftSideUserId = Number(leftSideUser?.id);

    return Number.isFinite(leftSideUserId) ? leftSideUserId : null;
  }

  private loadChatStartOptions(): void {
    this.apiService.getFakeUsers(1, 500).subscribe({
      next: (res) => {
        this.fakeUsersOptions = this.extractCollection(res);
      },
      error: (err) => console.log(err),
    });

    this.apiService.getAllUsers(1, "", 50).subscribe({
      next: (res) => {
        this.usersOptions = this.extractCollection(res).filter((user) => Number(user?.is_fake_user) !== 1);
      },
      error: (err) => console.log(err),
    });
  }

  private setDefaultSendAsUser(room: any, preferredUserId?: number): void {
    const users = this.toArray(room?.users);

    if (preferredUserId && users.some((user: any) => Number(user?.id) === Number(preferredUserId))) {
      this.sendAsUserId = Number(preferredUserId);
      return;
    }

    const fakeUser = users.find((user: any) => Number(user?.is_fake_user) === 1);
    if (fakeUser) {
      this.sendAsUserId = Number(fakeUser.id);
      return;
    }

    if (users.some((user: any) => Number(user?.id) === this.adminUserId)) {
      this.sendAsUserId = this.adminUserId;
      return;
    }

    const firstUserId = Number(users[0]?.id);
    this.sendAsUserId = Number.isFinite(firstUserId) ? firstUserId : null;
  }

  private toArray(value: any): any[] {
    if (!value) {
      return [];
    }

    if (Array.isArray(value)) {
      return value;
    }

    if (typeof value === "object") {
      return Object.values(value);
    }

    return [];
  }

  imageError(event: any) {
    event.target.src = "assets/images/icons/user.svg";
    event.target.style.objectFit = "contain";
    event.target.style.backgroundColor = "#fff";
    event.target.style.padding = "5px";
    event.target.style.borderRadius = "50%";
    event.target.style.border = "1px solid #7367f0";
  }

  onChatScroll() {
    // When user scrolls to the top, load older messages
    const el = this.userChats?.nativeElement;
    if (!el) return;
    if (el.scrollTop <= 10) {
      this.loadOlderMessages();
    }
  }

  scrollToBottom() {
    setTimeout(() => {
      if (this.userChats) {
        this.userChats.nativeElement.scrollTop =
          this.userChats.nativeElement.scrollHeight;
      }
    }, 0);
  }
}
